import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import styled, { css } from 'styled-components';
import { AlertHeader, Button, Checkbox, H3, LoaderSpin, ModalCloseButton, ModalContent, ModalFactory, ModalFooter, ModalText, Text } from 'components';
import { AnalyticsContext } from 'contexts/analytics';
import { FullStoryContext } from 'contexts/fullstory';
import { lineHeight, theme, toRem } from 'design';
import { selectCurrentEmployee } from 'store/App';
import { ITipCalculatorEmployeeTicket } from 'types/TipCalculator';
import { calculateTimeDifference, getDateTimeWithoutZone, toTitleCase } from 'utils';
import { centsToDollarsDisplay } from 'utils/currency';
import {
  ITipCalcValidationError,
  parseNoLaborForTicketsError,
  PosUnclosedChecksError,
  TipCalculationNoDataError,
  TipCalculationNoLaborForTicketsError,
  TipCalculationValidationError,
  TipCalculationValidationType,
  TipCalculatorFriendlyError,
} from 'utils/tipCalculator';
import { SettingsGateContent } from '../SettingsGateModal/SettingsGateModal';
import { useInitialWorkflow } from './hooks/useInitialWorkflow';
import { useRecalculateWorkflow } from './hooks/useRecalculateWorkflow';
import { IContentProps, ITipCalculatorProcessingModalProps, IWorkflowResult, TipCalculatorProcessingModalMode } from './types';

const Layout = styled.section`
  position: relative;
  z-index: ${theme.zIndex.modal + 2};
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  max-width: ${toRem(540)};
  height: auto;
  margin: ${toRem(theme.space['3xl'])} 0;
  padding: ${toRem(theme.space['3xl'])};
  border-radius: ${toRem(22)};
  background-color: ${theme.color.white};
  box-shadow: ${theme.shadow.modal};

  h3.modal-header-text {
    font-size: ${toRem(theme.font.size['2xl'])};
    line-height: ${lineHeight(theme.font.size['2xl'])};
  }

  h3.modal-header-text-error {
    color: ${theme.color.danger[50]};
    margin-top: ${toRem(theme.space.xl)};
  }

  .modal-text {
    margin-top: ${toRem(theme.space.md)};
    margin-bottom: ${toRem(theme.space.xl)};
  }

  .modal-step-message {
    margin-top: ${toRem(theme.space.xl)};
    text-align: center;
  }

  header {
    padding-bottom: 0;

    div {
      margin-bottom: 0;
    }
  }

  footer {
    padding-top: ${toRem(theme.space.xl)};

    button {
      width: 100%;
    }
  }
`;

const ErrorPanel = styled.div<{ alignCenter?: boolean }>`
  display: flex;
  flex-flow: column;
  width: 100%;
  padding: ${toRem(theme.space.xl)};
  border-radius: ${toRem(11)};
  background-color: ${theme.color.neutral[50]};
  margin-top: ${toRem(theme.space.md)};
  text-align: left;
  align-items: ${(props) => (props.alignCenter ? 'center' : 'flex-start')};

  h3 {
    margin-bottom: ${toRem(theme.space.sm)};
  }

  &.validation-error {
    h3 {
      margin-bottom: ${toRem(theme.space.sm)};
      text-transform: capitalize;
    }

    p {
      font-size: ${toRem(theme.font.size.md)};
      line-height: ${lineHeight(theme.font.size.md)};
    }

    &.alert {
      background-color: ${theme.color.warning[25]};
      border: ${toRem(2)} solid ${theme.color.warning[50]};
    }
  }
`;

const DetailCard = styled.div<{ noBorder: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${toRem(theme.space['2xs'])};
  align-self: stretch;
  margin: ${toRem(theme.space.sm)} 0 0 0;
  padding: ${toRem(theme.space.sm)} 0 ${toRem(theme.space.md)} 0;
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};

  ${({ noBorder }) =>
    noBorder &&
    css`
      border-bottom-width: 0;
      margin-bottom: 0;
      padding-bottom: 0;
    `}
`;

const Row = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const SubRow = styled(Row)`
  p {
    font-size: ${toRem(theme.font.size.xs)} !important;
    line-height: ${lineHeight(theme.font.size.xs)} !important;
    color: ${theme.color.neutral[500]};
  }
`;

const ItemList = styled.ul`
  list-style: none;
  padding: ${toRem(theme.space.md)} 0;
`;

const ListItem = styled.li`
  text-transform: capitalize;
  font-size: ${toRem(theme.font.size.md)};
  margin: ${toRem(theme.space.xs)} 0;
`;

const BulletList = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.sm)};
  padding: ${toRem(theme.space.md)};
`;

const BulletListItem = styled.li`
  font-size: ${toRem(theme.font.size.xs)};
  list-style: disc;
`;

const DataTable = styled.div`
  padding: ${toRem(theme.space.md)} 0;
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
`;

const DataCell = styled.div<{ alignRight?: boolean; header?: boolean }>`
  text-transform: capitalize;
  font-size: ${toRem(theme.font.size.md)};
  margin: ${toRem(theme.space.xs)} 0;
  width: 25%;
  word-break: break-word;
  text-align: left;
  justify-content: ${(props) => (props.alignRight ? 'flex-end' : undefined)};

  ${({ header }) =>
    header &&
    css`
      font-weight: ${theme.font.weight.semiBold};
    `}
`;

const PlaceholderText = styled.span`
  color: ${theme.color.neutral[400]};
  font-style: italic;
`;

const FieldGroup = styled.div`
  margin-top: ${toRem(theme.space.lg)};
  width: 100%;
`;

const renderErrorDetails = (dataCy: string, errors: unknown) => {
  if (!errors) return null;

  if (errors instanceof TipCalculatorFriendlyError) {
    return (
      <ErrorPanel data-cy={`${dataCy}-errors`}>
        <Text size="small" fontWeight="bold">
          {errors.title}
        </Text>
        <Text size="small">{errors.summary}</Text>
        {errors.items?.length > 0 && (
          <BulletList data-cy={`${dataCy}-validation-errors-list`}>
            {errors.items.map((item, index) => (
              <BulletListItem key={`friendly_error_${index}`}>{item.description}</BulletListItem>
            ))}
          </BulletList>
        )}
      </ErrorPanel>
    );
  }

  const errorObj = Array.isArray(errors) ? errors[0] : errors;
  if (!errorObj?.msg) return null;
  return (
    <ErrorPanel data-cy={`${dataCy}-errors`}>
      <H3 fontWeight="bold">Details</H3>
      <Text>{errorObj.msg}</Text>
    </ErrorPanel>
  );
};

interface ITicketErrorTableProps {
  dataCy: string;
  description: string;
  headerDataCy: string;
  renderOwner: (ticket: ITipCalculatorEmployeeTicket) => React.ReactNode;
  rowDataCyPrefix: string;
  rowKeyPrefix: string;
  tableDataCy: string;
  tickets: ITipCalculatorEmployeeTicket[];
  title: string;
}

const TicketErrorTable: React.FC<ITicketErrorTableProps> = ({ dataCy, description, headerDataCy, renderOwner, rowDataCyPrefix, rowKeyPrefix, tableDataCy, tickets, title }) => {
  if (tickets.length === 0) return null;

  return (
    <ErrorPanel data-cy={dataCy} className="validation-error">
      <H3 fontWeight="bold">{title}</H3>
      <Text>{description}</Text>
      <DataTable data-cy={tableDataCy}>
        <Row data-cy={headerDataCy}>
          <DataCell header>Ticket</DataCell>
          <DataCell header>Owner</DataCell>
          <DataCell header>Tip</DataCell>
          <DataCell header>Auto Gratuity</DataCell>
        </Row>
        {tickets.map((ticket, index) => (
          <Row key={`${rowKeyPrefix}-${ticket.ticketId}-${index}`} data-cy={`${rowDataCyPrefix}-${ticket.ticketId}`}>
            <DataCell>{ticket.displayId}</DataCell>
            <DataCell>{renderOwner(ticket)}</DataCell>
            <DataCell>{centsToDollarsDisplay(ticket.totals.tip.gross)}</DataCell>
            <DataCell>{centsToDollarsDisplay(ticket.totals.autoGratuity.gross)}</DataCell>
          </Row>
        ))}
      </DataTable>
    </ErrorPanel>
  );
};

const NoLaborForTicketsErrorPanel: React.FC<{ dataCy: string; errors: TipCalculationNoLaborForTicketsError; onSkipChange: (checked: boolean) => void }> = ({ dataCy, errors, onSkipChange }) => {
  const { ticketsWithEmployee, ticketsWithoutEmployee, hasSkippableErrors } = parseNoLaborForTicketsError(errors);

  return (
    <>
      <TicketErrorTable
        dataCy={`${dataCy}-no-labor-for-tickets-errors`}
        description='Clock entries are missing for the following employees. Please ensure the following employees have clocked in and clocked out and then click "Retry".'
        headerDataCy={`${dataCy}-no-labor-ticket-list-header`}
        renderOwner={(ticket) => `${ticket.employee.firstName} ${ticket.employee.lastName}`}
        rowDataCyPrefix={`${dataCy}-no-labor-ticket`}
        rowKeyPrefix="no-labor-ticket"
        tableDataCy={`${dataCy}-no-labor-ticket-list`}
        tickets={ticketsWithEmployee}
        title="No Labor For Tickets"
      />
      <TicketErrorTable
        dataCy={`${dataCy}-unassigned-tickets-errors`}
        description="We couldn't find the employee assigned to these tickets. Assign an owner to these tickets in your POS, then retry."
        headerDataCy={`${dataCy}-unassigned-tickets-list-header`}
        renderOwner={() => <PlaceholderText>Unknown</PlaceholderText>}
        rowDataCyPrefix={`${dataCy}-unassigned-ticket`}
        rowKeyPrefix="unassigned-ticket"
        tableDataCy={`${dataCy}-unassigned-tickets-list`}
        tickets={ticketsWithoutEmployee}
        title="Unassigned Tickets"
      />
      {hasSkippableErrors && (
        <FieldGroup data-cy={`${dataCy}-checkbox-wrapper`}>
          <Checkbox onChange={(e: ChangeEvent<HTMLInputElement>) => onSkipChange(e.target.checked)} id="skipNoLabor" name="skipNoLabor" data-cy={`${dataCy}-skip-no-labor`} size="small" label="Ignore no labor tickets" />
        </FieldGroup>
      )}
    </>
  );
};

const UnclosedChecksErrorPanel: React.FC<{ dataCy: string; errors: PosUnclosedChecksError }> = ({ dataCy, errors }) => (
  <>
    {errors.errors.map((error, i) => (
      <React.Fragment key={`error_${i}`}>
        <ErrorPanel data-cy={`${dataCy}-unclosed-checks-errors`} className="validation-error">
          <H3 fontWeight="bold">You have unclosed checks.</H3>
          <Text>Please apply the final tip amount and close any open checks, then try again.</Text>
          <DataTable data-cy={`${dataCy}-unclosed-checks-list`}>
            <Row data-cy={`${dataCy}-unclosed-checks-list-header`}>
              <DataCell header>Ticket</DataCell>
              <DataCell header>Owner</DataCell>
              <DataCell alignRight header>
                Amount
              </DataCell>
            </Row>
            {error.tickets?.map((ticket, index) => (
              <Row key={`${ticket.ticketId}-${index}`} data-cy={`${dataCy}-unclosed-checks-ticket-${ticket.ticketId}`}>
                <DataCell>{ticket.displayId}</DataCell>
                <DataCell>{ticket?.employee ? `${ticket.employee.firstName} ${ticket.employee.lastName}` : <PlaceholderText>Unknown</PlaceholderText>}</DataCell>
                <DataCell alignRight>{centsToDollarsDisplay(ticket.amount)}</DataCell>
              </Row>
            ))}
          </DataTable>
        </ErrorPanel>
      </React.Fragment>
    ))}
  </>
);

const InvalidTimeClockEntriesPanel: React.FC<{ dataCy: string; errors: TipCalculationValidationError; timeZone?: string }> = ({ dataCy, errors, timeZone }) => {
  const invalidTimeClockEntriesErrors = errors.errors.filter((e) => e.type === TipCalculationValidationType.EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES);
  if (invalidTimeClockEntriesErrors.length === 0) return null;

  return (
    <>
      {invalidTimeClockEntriesErrors.map((error, idx) => (
        <ErrorPanel key={`error_${error.type}_${idx}`} data-cy={`${dataCy}-validation-errors`} className="validation-error">
          <H3 fontWeight="bold">{error.title}</H3>
          <Text>{error.message}</Text>
          {error.employees.map((employee, idx) => (
            <DetailCard data-cy={`${dataCy}-employee-invalid-time-clock-entry`} key={`employee_${idx}`} noBorder={error.employees.length - 1 === idx}>
              <Row data-cy={`${dataCy}-employee-invalid-time-clock-entry-name`}>
                <Text fontWeight="bold">{employee.name}</Text>
                <Text fontWeight="bold">{toTitleCase(employee.jobTitle)}</Text>
              </Row>
              <SubRow data-cy={`${dataCy}-employee-invalid-time-clock-entry-clock-in`}>
                <Text>Clock In</Text>
                <Text>{getDateTimeWithoutZone(employee.start, timeZone)}</Text>
              </SubRow>
              <SubRow data-cy={`${dataCy}-employee-invalid-time-clock-entry-clock-out`}>
                <Text>Clock Out</Text>
                <Text>{getDateTimeWithoutZone(employee.end, timeZone)}</Text>
              </SubRow>
              <SubRow data-cy={`${dataCy}-employee-invalid-time-clock-entry-time-worked`}>
                <Text>Time Worked</Text>
                <Text>{calculateTimeDifference(employee.start, employee.end)}</Text>
              </SubRow>
            </DetailCard>
          ))}
        </ErrorPanel>
      ))}
    </>
  );
};

const ValidationErrorJobListPanel: React.FC<{ dataCy: string; errors: TipCalculationValidationError }> = ({ dataCy, errors }) => {
  const noTipsOrAutoGratuitiesToDistributeErrors = errors.errors.find((e) => e.type === TipCalculationValidationType.NO_TIPS_OR_AUTO_GRATUITIES_TO_DISTRIBUTE);

  // If there are no tips or auto gratuities to distribute, show the error panel.
  if (noTipsOrAutoGratuitiesToDistributeErrors) {
    return (
      <ErrorPanel data-cy={`${dataCy}-validation-errors`} alignCenter className="validation-error">
        <H3 fontWeight="bold">{noTipsOrAutoGratuitiesToDistributeErrors.title}</H3>
        <Text>{noTipsOrAutoGratuitiesToDistributeErrors.message}</Text>
      </ErrorPanel>
    );
  }

  const hasSpecialTypes = errors.errors.some((e) => e.type === TipCalculationValidationType.NO_TIP_SHARE_RULE_FOR_JOB || e.type === TipCalculationValidationType.EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES);

  const renderJobEmployeeItems = (jobIndex: number, job: { title: string; employees: { name: string; total: number }[] }, options: { showTotal: boolean; filterNonZeroTotals: boolean }) =>
    job.employees
      .filter((emp) => (options.filterNonZeroTotals ? emp.total > 0 : true))
      .map((employee, employeeIndex) => (
        <ListItem key={`$job_${jobIndex}_employee_${employeeIndex}`}>
          <b>{employee.name}</b> - {job.title}
          {options.showTotal ? <> - {centsToDollarsDisplay(employee.total)}</> : null}
        </ListItem>
      ));

  const renderItemList = (validationError: ITipCalcValidationError, options: { showTotal: boolean; filterNonZeroTotals: boolean }) => (
    <ItemList>{validationError.jobs?.map((job, i) => (typeof job === 'string' ? null : renderJobEmployeeItems(i, job, { showTotal: options.showTotal, filterNonZeroTotals: options.filterNonZeroTotals })))}</ItemList>
  );

  return (
    <>
      {errors.errors
        .filter((e) => e.type === TipCalculationValidationType.NO_TIP_SHARE_RULE_FOR_JOB)
        .map((error) => (
          <ErrorPanel key={`error_${error.type}`} data-cy={`${dataCy}-validation-errors`} className="validation-error">
            <H3 fontWeight="bold">{error.title}</H3>
            <Text>{error.message}</Text>
            {renderItemList(error, { showTotal: true, filterNonZeroTotals: true })}
          </ErrorPanel>
        ))}

      {errors.errors
        .filter((e) => e.type === TipCalculationValidationType.ZERO_POINT_RULE_FOR_JOB)
        .map((error) => (
          <ErrorPanel key={`error_${error.type}`} data-cy={`${dataCy}-validation-errors`} className="validation-error alert">
            <H3 fontWeight="bold">{error.title}</H3>
            <Text>{error.message}</Text>
            {renderItemList(error, { showTotal: true, filterNonZeroTotals: true })}
            <Text fontWeight="bold">For assistance, contact Kickfin Support.</Text>
          </ErrorPanel>
        ))}

      {!hasSpecialTypes &&
        errors.errors
          .filter((e) => e.type !== TipCalculationValidationType.NO_ELIGIBLE_EMPLOYEES && e.type !== TipCalculationValidationType.ZERO_POINT_RULE_FOR_JOB)
          .map((error) => (
            <ErrorPanel key={`error_${error.type}`} data-cy={`${dataCy}-validation-errors`} className="validation-error">
              <H3 fontWeight="bold">{error.title}</H3>
              <Text>{error.message}</Text>
              {renderItemList(error, { showTotal: false, filterNonZeroTotals: false })}
            </ErrorPanel>
          ))}
    </>
  );
};

const getErrorType = (errors: unknown): string => {
  if (errors instanceof TipCalculationNoLaborForTicketsError) return 'no_labor_for_tickets';
  if (errors instanceof PosUnclosedChecksError) return 'unclosed_checks';
  if (errors instanceof TipCalculationValidationError) return 'validation_error';
  if (errors instanceof TipCalculationNoDataError) return 'no_data';
  if (errors instanceof TipCalculatorFriendlyError) return 'friendly_error';
  if (Array.isArray(errors)) return 'api_error';
  return 'unknown';
};

const getValidationSubTypes = (errors: unknown): string | undefined => {
  if (errors instanceof TipCalculationValidationError) {
    return errors.errors.map((e) => e.type).join(',');
  }
  return undefined;
};

export const Content: React.FC<IContentProps> = ({ dataCy, businessDate, buttonText, message, stepMessage, status, errors, onClick, onSecondaryClick, onClose, timeZone, mode }) => {
  const [skipNoLabor, setSkipNoLabor] = useState<boolean>(undefined);
  const analytics = useContext(AnalyticsContext);
  const fullStory = useContext(FullStoryContext);
  const currentEmployee = useSelector(selectCurrentEmployee());
  const trackedErrorRef = useRef<string | null>(null);

  useEffect(() => {
    if ((status === 'error' || status === 'validationError') && errors) {
      const errorType = getErrorType(errors);
      const trackingKey = `${status}:${errorType}`;

      if (trackedErrorRef.current === trackingKey) return;
      trackedErrorRef.current = trackingKey;

      const organizationId = currentEmployee?.organizationId;
      const siteId = currentEmployee?.siteId;
      const validationTypes = getValidationSubTypes(errors);

      analytics?.fireEvent('tip_calculator_error_shown', { errorType, mode, businessDate, organizationId, siteId, validationTypes });

      fullStory?.trackEvent({
        name: 'Tip Calculator Error Shown',
        properties: { errorType, mode, businessDate, organizationId, siteId, validationTypes },
      });
    } else {
      trackedErrorRef.current = null;
    }
  }, [analytics, businessDate, currentEmployee, errors, fullStory, mode, status]);

  const handleClick = () => {
    onClick(skipNoLabor);
  };

  return (
    <Layout data-cy={`${dataCy}-content`}>
      <ModalCloseButton onClick={onClose} />
      <ModalContent>
        <H3 className="modal-header-text">{dayjs(businessDate).format('MMMM Do, YYYY')}</H3>
        <ModalText className="modal-text">{message}</ModalText>
        {status === 'loading' && <LoaderSpin size={60} stroke={5} color={theme.color.secondary[400]} />}
        {status === 'error' && <AlertHeader />}
        {status === 'success' && <AlertHeader icon="Success" iconFill={theme.color.secondary[400]} />}
        {stepMessage && <H3 className="modal-step-message">{stepMessage}</H3>}

        {errors instanceof TipCalculationNoLaborForTicketsError && <NoLaborForTicketsErrorPanel dataCy={dataCy} errors={errors} onSkipChange={setSkipNoLabor} />}
        {errors instanceof PosUnclosedChecksError && <UnclosedChecksErrorPanel dataCy={dataCy} errors={errors} />}
        {errors instanceof TipCalculationValidationError && (
          <>
            <InvalidTimeClockEntriesPanel dataCy={dataCy} errors={errors} timeZone={timeZone} />
            <ValidationErrorJobListPanel dataCy={dataCy} errors={errors} />
          </>
        )}
        {errors instanceof TipCalculationNoDataError && (
          <ErrorPanel data-cy={`${dataCy}-errors`}>
            <H3 fontWeight="bold">Details</H3>
            <Text>{errors.message}</Text>
          </ErrorPanel>
        )}
        {renderErrorDetails(dataCy, errors)}
      </ModalContent>
      <ModalFooter columns={onSecondaryClick ? 2 : 1}>
        {onSecondaryClick && <Button data-cy={`${dataCy}-secondary-btn`} label="Back" type="button" appearance="white" size="large" onClick={onSecondaryClick} isProcessing={false} disabled={status === 'loading'} />}
        <Button data-cy={`${dataCy}-action-btn`} label={buttonText} type="button" appearance="primary" size="large" onClick={handleClick} isProcessing={false} disabled={status === 'loading'} />
      </ModalFooter>
    </Layout>
  );
};

export const TipCalculatorProcessingModal: React.FC<ITipCalculatorProcessingModalProps> = (props) => {
  const { contentProps, timeZone, settingsGate }: IWorkflowResult = props.mode === TipCalculatorProcessingModalMode.RECALCULATE ? useRecalculateWorkflow(props) : useInitialWorkflow(props);

  return (
    <ModalFactory customModal onClose={settingsGate ? settingsGate.onCancel : props.onClose} isActive closeOnOutSideClick={false}>
      {settingsGate ? (
        <SettingsGateContent state={settingsGate.state} onCancel={settingsGate.onCancel} onCalculate={settingsGate.onCalculate} />
      ) : (
        <Content
          dataCy={`tip-calculator-processing-${props.mode}-modal`}
          businessDate={props.businessDate}
          buttonText={contentProps.buttonText}
          errors={contentProps.errors}
          message={contentProps.message}
          onClick={contentProps.onClick}
          onClose={props.onClose}
          onSecondaryClick={contentProps.onSecondaryClick}
          stepMessage={contentProps.stepMessage}
          status={contentProps.status}
          timeZone={timeZone}
          mode={props.mode}
        />
      )}
    </ModalFactory>
  );
};
